import { createReducer } from '@reduxjs/toolkit';
import { OrderDto, IOrderDto, OrderStatus, ClientDto, SiteDto } from '../../api/ClientApi';
import { myAssert } from '../../common/console';

import {
    createNewEmptyOrder,
    createNewEmptyReceptionOrder,
    createNewOrder,
    clearOrder,
    saveOrder,
    importOrder,
    addOrderGoodsId,
    removeDestinationSite,
    removeOrderGoodsId,
    removeOriginSite,
    removeSupplier,
    setDestinationSite,
    setOrderStatus,
    setOriginSite,
    setSupplier,
    updateOrderData,
    updateOrderDescription,
} from '../actions/orderActions';
import {
    clearBaseEntityState,
    compareModifiedDate,
    IBaseEntityState,
    IModifiedInfo,
    SaveEntityStatus,
    setBaseEntityCurrentJson,
} from './common';

export interface OrderFormReducerState extends IBaseEntityState {
    order: IOrderDto | null;
}

function clearState(state: OrderFormReducerState): void {
    state.order = null;

    clearBaseEntityState(state);
}

function getInitialState(): OrderFormReducerState {
    const state: OrderFormReducerState = {
        order: null,
        baseJsonString: null,
        currentJsonString: null,
        isChanged: undefined,
        saveStatus: SaveEntityStatus.None,
        modified: undefined,
    };

    clearState(state);

    return state;
}

function calculateCurrentJson(state: OrderFormReducerState, setAsBaseline: boolean = false): void {
    myAssert(() => state.order != null, 'Need order to calculate');

    const jsonBaseline = JSON.stringify(state.order);

    setBaseEntityCurrentJson(state, jsonBaseline, setAsBaseline);
}

function calculateBaseInfo(state: OrderFormReducerState) {
    myAssert(() => state.order != null, 'Need order to calculate');

    if (!state.order!.id || state.order!.id <= 0) {
        // no modified data should be set
    } else {
        // lets find the last modified part of order
        const modified: IModifiedInfo | undefined = compareModifiedDate(state.order, undefined);

        state.modified =
            (modified ?? state.order!.created)
                ? { modifiedAt: state.order!.created ?? new Date(), modifiedBy: state.order!.createdBy ?? '' }
                : undefined;
    }
}

export const orderFormReducer = createReducer<OrderFormReducerState>(
    getInitialState(), // Initial state
    (builder) => {
        builder
            .addCase(createNewEmptyOrder, (state: OrderFormReducerState, action: any) => {
                clearState(state);

                const newOrder: IOrderDto = new OrderDto();

                newOrder.id = 0;
                newOrder.orderStatus = OrderStatus.Idle;

                state.order = newOrder;

                calculateBaseInfo(state);
                calculateCurrentJson(state);
            })
            .addCase(createNewEmptyReceptionOrder, (state: OrderFormReducerState, action: any) => {
                clearState(state);

                const newOrder: IOrderDto = new OrderDto();

                newOrder.id = 0;
                newOrder.orderStatus = OrderStatus.Transport;

                state.order = newOrder;

                calculateBaseInfo(state);
                calculateCurrentJson(state);
            })
            .addCase(createNewOrder, (state: OrderFormReducerState, action: any) => {
                clearState(state);

                const payload: IOrderDto = action.payload;

                // check order data
                myAssert(() => payload.id === 0, 'It is new order!!!');

                state.order = payload;

                calculateBaseInfo(state);
                calculateCurrentJson(state);
            })
            .addCase(importOrder.fulfilled, (state: OrderFormReducerState, action: any) => {
                clearState(state);

                const payload: IOrderDto = action.payload;

                myAssert(() => !!payload && !!payload.orderStatus, 'Required Data for Import');

                state.order = payload;

                calculateBaseInfo(state);
                calculateCurrentJson(state, true);
            })
            .addCase(clearOrder, (state: OrderFormReducerState, action: any) => {
                clearState(state);
            })
            .addCase(addOrderGoodsId, (state: OrderFormReducerState, action: any) => {
                myAssert(() => state.order !== null, 'There needs to be order if it is being updated');

                if (!!state.order) {
                    state.order! = { ...state.order, goodsIds: [...(state.order!.goodsIds ?? []), action.payload] };
                }

                calculateCurrentJson(state);
            })
            .addCase(removeOrderGoodsId, (state: OrderFormReducerState, action: any) => {
                myAssert(() => state.order !== null, 'There needs to be order if it is being updated');

                if (!!state.order && !!state.order.goodsIds) {
                    state.order! = {
                        ...state.order,
                        goodsIds: state.order.goodsIds.filter((gId) => gId !== action.payload),
                    };
                }

                calculateCurrentJson(state);
            })
            .addCase(setSupplier, (state: OrderFormReducerState, action: any) => {
                myAssert(() => state.order !== null, 'There needs to be order if it is being updated');

                const supp = action.payload as ClientDto;

                state.order! = { ...state.order, supplier: supp, supplierId: supp?.id };

                calculateCurrentJson(state);
            })
            .addCase(removeSupplier, (state: OrderFormReducerState, action: any) => {
                myAssert(() => state.order !== null, 'There needs to be order if it is being updated');

                state.order! = { ...state.order, supplier: undefined, supplierId: undefined };

                calculateCurrentJson(state);
            })
            .addCase(setOriginSite, (state: OrderFormReducerState, action: any) => {
                myAssert(() => state.order !== null, 'There needs to be order if it is being updated');

                const site = action.payload as SiteDto;

                state.order! = { ...state.order, originSite: site, originSiteId: site?.id };

                calculateCurrentJson(state);
            })
            .addCase(removeOriginSite, (state: OrderFormReducerState, action: any) => {
                myAssert(() => state.order !== null, 'There needs to be order if it is being updated');

                state.order! = { ...state.order, originSite: undefined, originSiteId: undefined };

                calculateCurrentJson(state);
            })
            .addCase(setDestinationSite, (state: OrderFormReducerState, action: any) => {
                myAssert(() => state.order !== null, 'There needs to be order if it is being updated');

                const site = action.payload as SiteDto;

                state.order! = { ...state.order, destinationSite: site, destinationSiteId: site?.id };

                calculateCurrentJson(state);
            })
            .addCase(removeDestinationSite, (state: OrderFormReducerState, action: any) => {
                myAssert(() => state.order !== null, 'There needs to be order if it is being updated');

                state.order! = { ...state.order, destinationSite: undefined, destinationSiteId: undefined };

                calculateCurrentJson(state);
            })
            .addCase(setOrderStatus, (state: OrderFormReducerState, action: any) => {
                myAssert(() => state.order !== null, 'There needs to be order if it is being updated');

                state.order! = { ...state.order, orderStatus: action.payload };
                calculateCurrentJson(state);
            })
            .addCase(updateOrderDescription, (state: OrderFormReducerState, action: any) => {
                myAssert(() => state.order !== null, 'There needs to be order if it is being updated');

                state.order! = { ...state.order, description: action.payload };
                calculateCurrentJson(state);
            })
            .addCase(updateOrderData, (state: OrderFormReducerState, action: any) => {
                myAssert(() => state.order !== null, 'There needs to be order if it is being updated');

                state.order! = { ...state.order, data: action.payload };
                calculateCurrentJson(state);
            })
            .addCase(saveOrder.pending, (state: OrderFormReducerState, action: any) => {
                myAssert(() => state.order !== null, 'There needs to be order if save is on its way');
                myAssert(
                    () => state.saveStatus === SaveEntityStatus.None || state.saveStatus === SaveEntityStatus.Error,
                    '',
                    false,
                );

                state.saveStatus = SaveEntityStatus.Pending;
            })
            .addCase(saveOrder.fulfilled, (state: OrderFormReducerState, action: any) => {
                myAssert(() => state.order !== null, 'There needs to be order if save is on its way');
                myAssert(() => state.saveStatus === SaveEntityStatus.Pending, '', false);

                state.saveStatus = SaveEntityStatus.Saved;

                calculateBaseInfo(state);
                calculateCurrentJson(state, true);
            })
            .addCase(saveOrder.rejected, (state: OrderFormReducerState, action: any) => {
                myAssert(() => state.order !== null, 'There needs to be order if save is on its way');
                myAssert(() => state.saveStatus === SaveEntityStatus.Pending, '', false);

                state.saveStatus = SaveEntityStatus.Error;
            });
    },
);
